//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import { apis } from 'utils/lib/apis';
import { flatMap, map, filter, has, get, uniqBy } from "lodash"
import moment from 'moment';
import config from '../../config';
//  -- Actions ------------------------
import * as SubmissionsActions from './actions';
import { logToSentry } from "utils/networkErrorUtils";
import * as Sentry from "@sentry/react";
import { getCurrentSemStartAndEndDate } from 'utils/methods/math';
import dayjs from 'utils/dayjsConfig';

//  ----------------------------------------------------------------------------
//  Thunks
//  ----------------------------------------------------------------------------

//  -- Create submission --------------

export const handleCreateSubmission = (data) => async(dispatch) => {
    await dispatch(SubmissionsActions.createSubmission());
    try {
        const submission = await apis.post('assignmentsRDS', `/assignment`, {
            body: { ...data }
        });
        
        if (submission && submission.error) {
            throw submission.error;
        }
        
        const result = submission.data || submission;
        const assignment = { ...result , assignment:data.assignment}
        await dispatch(SubmissionsActions.createSubmissionSuccess(assignment));
        return result 
    } catch (error) {
        console.error('Error creating submission:', error);
        if(error&&error.message!="Network Error"){
            logToSentry({
                message: 'Error creating submission',
                error,
                module: 'SUBMISSIONS',
                operation: 'CREATE_STORY'
            });
         }
        await dispatch(SubmissionsActions.createSubmissionFail(error));
    }
};



//  -- List class submissions ---------
export const handleListClassSubmissions = (classID,returnVal,parentOrgId,loadPreviousSem=false,isApGradeBook=false) => async(dispatch, getState) => {
    await dispatch(SubmissionsActions.listClassSubmissions());
   if(!classID) return;
   const SUBMISSION_LENGTH = 200
    try {
        const fallbackParentOrgId = get(getState(), "currentOrg.parent.id");
        const resolvedParentOrgId = parentOrgId || fallbackParentOrgId;
        const getCurrentSem =  getCurrentSemStartAndEndDate(resolvedParentOrgId);
        const startDate = dayjs(getCurrentSem.start).toISOString();
        const endDate = dayjs(getCurrentSem.end).toISOString();
        const loadCurrentSemData = startDate && endDate && !loadPreviousSem 
        const urlPath = loadCurrentSemData ? `/getSubmissionLengthByClassId/${classID}?startDate=${startDate}&endDate=${endDate}&isApGradeBook=${isApGradeBook}`:`/getSubmissionLengthByClassId/${classID}?isApGradeBook=${isApGradeBook}`;
         const numberOfSubmission = await apis.get('assignmentsRDS', urlPath);
        
        if (numberOfSubmission && numberOfSubmission.error) {
            throw numberOfSubmission.error;
        }
        
        const countData = numberOfSubmission?.data || numberOfSubmission;
        const rawCount = typeof countData === 'number' ? countData : get(countData, 'length', 0);
        const count = Number(rawCount);
        
        if (Number.isFinite(count) && count > 0) {
            const submissionArr = Array.apply(0, new Array(Math.ceil(count / SUBMISSION_LENGTH))).map((_k, i) => i)
            const allSubmission = await Promise.allSettled(map(submissionArr, async k => {
                const dataPath = loadCurrentSemData ? `/submissionByClassLite/${classID}/${k * SUBMISSION_LENGTH}/${SUBMISSION_LENGTH}?startDate=${startDate}&endDate=${endDate}&isApGradeBook=${isApGradeBook}`:`/submissionByClassLite/${classID}/${k * SUBMISSION_LENGTH}/${SUBMISSION_LENGTH}?isApGradeBook=${isApGradeBook}`;
                const chunkData = await apis.get('assignmentsRDS', dataPath);
                if (chunkData && chunkData.error) {
                    throw chunkData.error;
                }
                return chunkData.data || chunkData;
            })
            ).then(v => filter(v, ["status", "fulfilled"]).map(k => k.value))
            const allSubmissionData = uniqBy(flatMap(allSubmission), 'id');
            await dispatch(SubmissionsActions.listClassSubmissionsSuccess(allSubmissionData));
            return allSubmissionData;
        } else {
            const submissionDataRDS = await apis.get('assignmentsRDS', `/assignmentsByClass/${classID}`);
            
            if (submissionDataRDS && submissionDataRDS.error) {
                throw submissionDataRDS.error;
            }
            
            const result = submissionDataRDS.data || submissionDataRDS;
            let submissionData = []
            if (result.length > 0) {
                submissionData = uniqBy(submissionData.concat(result), 'id')
            }
            if (returnVal) {
                return submissionData;
            }
            await dispatch(SubmissionsActions.listClassSubmissionsSuccess(submissionData));
            return submissionData;
        }
        
    } catch (error) {
        console.error('Error listing class submissions:', error);
        if(error&&error.message!="Network Error"){
            logToSentry({
                message: 'Error listing class submissions',
                error,
                module: 'SUBMISSIONS',
                operation: 'LIST_CLASS_STUDENT'
            });
         }
        await dispatch(SubmissionsActions.listClassSubmissionsFail(error));
    }
};

export const handleGetSubmissionFull = (submissionId) => async (dispatch) => {
    if (!submissionId) return null;
    try {
        const submission = await apis.get('assignmentsRDS', `/submissionFull/${submissionId}`);
        if (submission && submission.error) {
            throw submission.error;
        }
        const result = submission.data || submission;
        if (has(result, "id") && result.id) {
            await dispatch(SubmissionsActions.updateSubmissionSuccess(result));
        }
        return result || null;
    } catch (error) {
        console.error('Error fetching full submission:', error);
        if (error && error.message !== 'Network Error') {
            logToSentry({
                message: 'Error fetching full submission',
                error,
                module: 'SUBMISSIONS',
                operation: 'GET_SUBMISSION_FULL'
            });
        }
        return null;
    }
};

export const handleListClassSubmissionsBySem = (classID,startDate,endDate) => async(dispatch) => {
   if(!classID) return;
   const SUBMISSION_LENGTH = 100
    try {
        
        const numberOfSubmission = await apis.post('assignmentsRDS', `/getSubmissionLengthByClassId`, {
          body: { 
            classId: classID,
            startDate: startDate,
            endDate: endDate
        }
        });
        
        if (numberOfSubmission && numberOfSubmission.error) {
            throw numberOfSubmission.error;
        }
        
        const countData = numberOfSubmission.data || numberOfSubmission;
        const count = typeof countData === 'number' ? countData : countData?.length || countData;
        
        if (count && count.length) {
            const submissionArr = Array.apply(0, new Array(Math.floor(count.length / SUBMISSION_LENGTH) + 1)).map((_k, i) => i)
            const allSubmission = await Promise.allSettled(map(submissionArr, async k => {
                const chunkData = await apis.post('assignmentsRDS', `/submissionByClass`,{
                     body:{
                        classId: classID,
                        offset:k * SUBMISSION_LENGTH,
                        limit: SUBMISSION_LENGTH,
                        startDate: startDate,
                        endDate: endDate
                     }
                });
                if (chunkData && chunkData.error) {
                    throw chunkData.error;
                }
                return chunkData.data || chunkData;
            })
            ).then(v => filter(v, ["status", "fulfilled"]).map(k => k.value))
            const allSubmissionData = flatMap(allSubmission);
            await dispatch(SubmissionsActions.listClassSubmissionsSuccess(allSubmissionData));
            return allSubmissionData;
        }
        
    } catch (error) {
        console.error('Error listing class submissions by sem:', error);
        if(error&&error.message!="Network Error"){
            logToSentry({
                message: 'Error listing class submissions by sem',
                error,
                module: 'SUBMISSIONS',
                operation: 'LIST_CLASS_STUDENT_BOOK_STUDENT'
            });
         }
        return [];
    }
};

//  -- List user submissions ----------
export const handleListUserSubmissions = (userID,cb) => async(dispatch) => {
    await dispatch(SubmissionsActions.listUserSubmissions());
    try {
        const submissionData=[];
        const submissionsRDS = await apis.get('assignmentsRDS', `/assignmentsByUser/${userID}`);
        
        if (submissionsRDS && submissionsRDS.error) {
            throw submissionsRDS.error;
        }
        
        const result = submissionsRDS.data || submissionsRDS;
        if(Array.isArray(result)&& result.length>0){
            submissionData.push(...result)
        }
       if(cb){
           return submissionData;
       }
        await dispatch(SubmissionsActions.listUserSubmissionsSuccess(submissionData));
    } catch (error) {
        console.error('Error listing user submissions:', error);
        if(error&&error.message!="Network Error"){
            logToSentry({
                message: 'Error listing user submissions',
                error,
                module: 'SUBMISSIONS',
                operation: 'LIST_USER_STUDENT'
            });
         }
        await dispatch(SubmissionsActions.listUserSubmissionsFail(error));
    }
};
// Request deduplication map to prevent concurrent identical API calls
const pendingRequests = new Map();

export const handleListUserSubmissionsByClass = (userId, classId, for_teacher = false, loadPreviousSem = false) => async (dispatch, getState) => {
    const requestKey = for_teacher
        ? `${userId}-${classId}-${for_teacher}-${loadPreviousSem}`
        : `${userId}-${classId}-${for_teacher}`;

    if (pendingRequests.has(requestKey)) {
        return pendingRequests.get(requestKey);
    }

    const request = (async () => {
        await dispatch(SubmissionsActions.listUserSubmissions());
        try {
            let submissionData = [];
            const requestBody = {
                classId,
                userId,
            };
            if (for_teacher) {
                const resolvedParentOrgId = get(getState(), 'currentOrg.parent.id');
                const getCurrentSem = getCurrentSemStartAndEndDate(resolvedParentOrgId);
                const startDate = dayjs(getCurrentSem.start).toISOString();
                const endDate = dayjs(getCurrentSem.end).toISOString();
                const loadCurrentSemData = Boolean(startDate && endDate && !loadPreviousSem);
                if (loadCurrentSemData) {
                    requestBody.startDate = startDate;
                    requestBody.endDate = endDate;
                }
            }

            const submissionsRDS = await apis.post('assignmentsRDS', `/user_submission_by_class`, {
                body: requestBody,
            });
            if (submissionsRDS && submissionsRDS.error) {
                throw submissionsRDS.error;
            }
            
            if (submissionsRDS && submissionsRDS.success) {
                submissionData = submissionsRDS.data;
                if(for_teacher){
                    if (Array.isArray(submissionData)) {
                        await dispatch(SubmissionsActions.updateSubmissionInBatchSuccess(submissionData));
                    }
                }else{
                    await dispatch(SubmissionsActions.listUserSubmissionsSuccess(submissionData));
                }
            }
            return submissionData;
        } catch (error) {
            console.error('Error listing user submissions by class:', error);
            if (error && error.message != "Network Error") {
                logToSentry({
                message: 'Error listing user submissions by class',
                error,
                module: 'SUBMISSIONS',
                operation: 'LIST_USER_STUDENT_BOOK_CLASS'
            });
            }
            throw error;
        } finally {
            // Always clean up the pending request, regardless of success/failure
            pendingRequests.delete(requestKey);
        }
    })();
    
    // Store the promise in the map for deduplication
    pendingRequests.set(requestKey, request);
    return request;
};

//  -- Update submission --------------
export const handleUpdateSubmission = (submissionID, data,tempData) => async(dispatch) => {
    await dispatch(SubmissionsActions.updateSubmission());
    let _submission =null
    let submissiom_id=submissionID
    let postData=data;
    let cDate= moment().valueOf()
    if(typeof tempData=="object"){
        tempData.complete=true;
    }
    if(typeof data.complete != "boolean"){
        data.complete=true;
    }
     try {
         if(!submissionID&&tempData){
             _submission =  await apis.post('assignmentsRDS', `/assignment`, {
                body: { ...tempData }
               });
         }
     } catch (error) {
         console.error("Error in create submission at the time of updatation",error);
     }
    try {
        if(!submissiom_id&&_submission){
            if(_submission.id){
                submissiom_id=_submission.id
                tempData.id = _submission.id
                postData=tempData
            }else if(_submission.data&&_submission.data.id){
                submissiom_id=_submission.data.id
                tempData.id = _submission.data.id
                postData=tempData
            }
        }
      if(!submissiom_id&&!_submission){
          return
      }
        postData.createdAt= cDate;
        const submission = await apis.put('assignmentsRDS', `/assignments/${submissiom_id}`, {
            body: { ...postData }
        });
        
        if (submission && submission.error) {
            throw submission.error;
        }
        
        const result = submission.data || submission;
        const assignment = data?.assignment ? data.assignment : tempData?.assignment
        const user_data =  data?.user ? data.user : tempData?.user
        if (!submissionID) {
            await dispatch(SubmissionsActions.createSubmissionSuccess(({ ...result, assignment:assignment , user:user_data })));
        } else {
            await dispatch(SubmissionsActions.updateSubmissionSuccess({ ...result, assignment: assignment , user:user_data }));
        }
        return result
    } catch (error) {
        console.error('Error updating submission:', error);
        if(error&&error.message!="Network Error"){
            logToSentry({
                message: 'Error updating submission',
                error,
                module: 'SUBMISSIONS',
                operation: 'UPDATE_STORY'
            });
         }
        await dispatch(SubmissionsActions.updateSubmissionFail(error));
        return null;
    }
};


const BATCH_GRADE_DEBOUNCE_MS = 800;

let batchQueue = [];
let batchTimeout = null;
let flushChain = Promise.resolve();

const mergeIntoBatchQueue = (data) => {
    const hasValueIndex = data && data.id ? batchQueue.findIndex(k => k.id == data.id) : -1;
    if (hasValueIndex > -1) {
        batchQueue[hasValueIndex] = data;
        return;
    }
    if (data.assignmentId && data.classId && data.userId) {
        const hasValueIndexFornonId = batchQueue.findIndex(k => k.assignmentId == data.assignmentId && k.classId == data.classId && k.userId == data.userId);
        if (hasValueIndexFornonId > -1) {
            batchQueue[hasValueIndexFornonId] = data;
            return;
        }
    }
    batchQueue.push(data);
};

const runBatchGradeFlush = async (dispatch) => {
    if (batchQueue.length === 0) return;
    const payload = [...batchQueue];
    batchQueue = [];
    try {
        const submission = await apis.post('assignmentsRDS', `/batchGradeUpdates`, {
            body: payload,
        });

        if (submission && submission.error) {
            throw submission.error;
        }

        if (submission && submission.success && Array.isArray(submission.data)) {
            const to_update = submission.data.filter((sub) => sub && sub.id);
            dispatch(SubmissionsActions.updateSubmissionInBatchSuccess(to_update));
        }
    } catch (error) {
        batchQueue = [...payload, ...batchQueue];
        console.error('Error batch grading submissions:', error);
        if (error && error.message !== "Network Error") {
            logToSentry({
                message: 'Error batch grading submissions',
                error,
                module: 'SUBMISSIONS',
                operation: 'B_G_S'
            });
        }
    }
};

const enqueueBatchGradeFlush = (dispatch) => {
    flushChain = flushChain.then(() => runBatchGradeFlush(dispatch)).catch(() => {});
    return flushChain;
};

export const handleBatchGradeSubmissions = (data, options = {}) => async (dispatch) => {
    const { immediate = false } = options;
    mergeIntoBatchQueue(data);
    if (immediate) {
        if (batchTimeout) {
            clearTimeout(batchTimeout);
            batchTimeout = null;
        }
        await enqueueBatchGradeFlush(dispatch);
        return;
    }
    if (batchTimeout) {
        clearTimeout(batchTimeout);
    }
    batchTimeout = setTimeout(() => {
        batchTimeout = null;
        void enqueueBatchGradeFlush(dispatch);
    }, BATCH_GRADE_DEBOUNCE_MS);
};


export const handleUpdateMetSubmission = (data) => async(dispatch) => {
    try {
        const submission = await apis.put('assignmentsRDS', `/updateMetSubmission`, {
            body:data
        });
        
        if (submission && submission.error) {
            throw submission.error;
        }
        
        const result = submission.data || submission;
        if(has(result,"id")){
            await dispatch(SubmissionsActions.updateSubmissionSuccess(result));
        }
        return result || {};
    } catch (error) {
        console.error('Error updating met submission:', error);
        if(error&&error.message!="Network Error"){
            logToSentry({
                message: 'Error updating met submission',
                error,
                module: 'SUBMISSIONS',
                operation: 'UPDATE_MESSAGE_STUDENT'
            });
         }
        return {};
    }
};
export const handleAiFeedback = (submission_id, language_feedback) => async(dispatch) => {
    try {
        const submission = await apis.post('assignmentsRDS', `/schedule_ai_feedback`, {
            body:{
                "submission_id":submission_id,
                "language_feedback" : language_feedback
            }
        });
        
        if (submission && submission.error) {
            throw submission.error;
        }
        
        const result = submission.data || submission;
        if(has(result,"id")){
            await dispatch(SubmissionsActions.updateSubmissionSuccess(result));
        }
        return result || {};
    } catch (error) {
        console.error('Error scheduling ai feedback:', error);
        if(error&&error.message!="Network Error"){
            logToSentry({
                message: 'Error scheduling ai feedback',
                error,
                module: 'SUBMISSIONS',
                operation: 'ADD_FEEDBACK'
            });
         }
        return {};
    }
};
export const handleAiAutoGrading = (submission_id, language_feedback) => async (dispatch) => {
    try {
        const submission = await apis.post('assignmentsRDS', `/ai_grading`, {
            body: {
                "submission_id": submission_id,
                "language_feedback": language_feedback
            }
        });
        
        if (submission && submission.error) {
            throw submission.error;
        }
        
        const result = submission.data || submission;
        if (has(result, "id")) {
            await dispatch(SubmissionsActions.updateSubmissionSuccess(result));
        }
        return result || {};
    } catch (error) {
        console.error('Error ai auto grading:', error);
        if (error && error.message != "Network Error") {
            logToSentry({
                message: 'Error ai auto grading',
                error,
                module: 'SUBMISSIONS',
                operation: 'ADD_ACTIVITY_GROUP'
            });
        }
        return {};
    }
};
export const handleAiAutoGradingSchedule = (submission_id, language_feedback,force_update , waiting_for_ai_grade, grade_level) => async (dispatch) => {
    try {
        const submission = await apis.post('assignmentsRDS', `/schedule_ai_grading`, {
            body: {
                "submission_id": submission_id,
                "language_feedback": language_feedback,
                force_update,
                waiting_for_ai_grade:waiting_for_ai_grade,
                ai_grade_level: grade_level
            }
        });
        
        if (submission && submission.error) {
            throw submission.error;
        }
        
        const result = submission.data || submission;
        if (has(result, "submission")&&result.submission&&result.submission.id) {
            await dispatch(SubmissionsActions.updateSubmissionSuccess(result.submission));
        }
        return result || {};
    } catch (error) {
        console.error('Error scheduling ai auto grading:', error);
        if (error && error.message != "Network Error") {
            logToSentry({
                message: 'Error scheduling ai auto grading',
                error,
                module: 'SUBMISSIONS',
                operation: 'ADD_ACTIVITY_GROUP_STUDENT'
            });
        }
        return {};
    }
};

export const handleAiStandardFeedback = (submission_id, language_feedback) => async(dispatch) => {
    try {
        const submission = await apis.post('assignmentsRDS', `/schedule_ai_standard_feedback`, {
            body:{
                "submission_id":submission_id,
                "language_feedback" : language_feedback
            }
        });
        
        if (submission && submission.error) {
            throw submission.error;
        }
        
        const result = submission.data || submission;
        if(has(result,"id")){
            await dispatch(SubmissionsActions.updateSubmissionSuccess(result));
        }
        return result || {};
    } catch (error) {
        console.error('Error scheduling ai standard feedback:', error);
        if(error&&error.message!="Network Error"){
            logToSentry({
                message: 'Error scheduling ai standard feedback',
                error,
                module: 'SUBMISSIONS',
                operation: 'ADD_FEEDBACK'
            });
            Sentry.captureException(`1098 Error ai standard feedback: ${error}`);
         }
        return {};
    }
};
export const handleUpdateSubmissionsToAll  = (submissions,classId) => async(dispatch) => {
    try {
     const submission =  await apis.put('assignmentsRDS', `/updateToAllGrade`, {
         body: { submissions  }
        });
        
        if (submission && submission.error) {
            throw submission.error;
        }
 
      await dispatch(handleListClassSubmissions(classId))  
      const result = submission.data || submission;
      return result || {};
    } catch (error) {
        console.error('Error updating submissions to all:', error);
        if (error && error.message != "Network Error") {
            logToSentry({
                message: 'Error updating submissions to all',
                error,
                module: 'SUBMISSIONS',
                operation: 'UPDATE_SUBMISSIONS'
            });
        }
        return {};
    }
 };
export const handleReturnSubmission = (submissionID) => async (dispatch) => {
    await dispatch(SubmissionsActions.updateSubmission());

    try {
        const submission = await apis.put('assignmentsRDS', `/returnSubmission/${submissionID}`, {
        });
        
        if (submission && submission.error) {
            throw submission.error;
        }
        
        const result = submission.data || submission;
        if (has(result, "id")) {
            await dispatch(SubmissionsActions.updateSubmissionSuccess(result));
        }
        return result || {};
    } catch (error) {
        console.error('Error returning submission:', error);
        if (error && error.message != "Network Error") {
            logToSentry({
                message: 'Error returning submission',
                error,
                module: 'SUBMISSIONS',
                operation: 'REMOVE_STUDENT'
            });
        }
        await dispatch(SubmissionsActions.updateSubmissionFail(error));
        return null;
    }
};
export const handleUpdateGrade = (data) => async (dispatch) => {
    try {
        const submission = await apis.put('assignmentsRDS', `/updateGrade`, {
            body: data
        });
        
        if (submission && submission.error) {
            throw submission.error;
        }
        
        const result = submission.data || submission;
        if(result){
            await dispatch(SubmissionsActions.updateSubmissionSuccess(result));
            return result
        }
        return {};
    } catch (error) {
        console.error('Error updating grade:', error);
        if (error && error.message != "Network Error") {
            logToSentry({
                message: 'Error updating grade',
                error,
                module: 'SUBMISSIONS',
                operation: 'UPDATE_GROUP'
            });
        }
        await dispatch(SubmissionsActions.updateSubmissionFail(error));
        return null;
    }
}
export const handleUpdateApGrade = (data) => async (dispatch) => {
    try {
        const submission = await apis.post('assignmentsRDS', `/updateApGrade`, {
            body: data
        });
        
        if (submission && submission.error) {
            throw submission.error;
        }
        
        const result = submission.data || submission;
        if(result){
            await dispatch(SubmissionsActions.updateSubmissionSuccess(result));
            return result
        }
        return {};
    } catch (error) {
        console.error('Error updating ap grade:', error);
        if (error && error.message != "Network Error") {
            logToSentry({
                message: 'Error updating ap grade',
                error,
                module: 'SUBMISSIONS',
                operation: 'CREATE_SUBMISSION'
            })
        }
        await dispatch(SubmissionsActions.updateSubmissionFail(error));
        return null;
    }
}
export const handleAudioTranscript = async (url) => {
    try {
        const response = await apis.post('assignmentsRDS', `/audio_transcripting`, {
            body: {
                "audioUrl": url,
                "languageCode": "es-US",
                "bucket":config.isDev ?"aws-audio-transcript" : "aws-audio-transcript-prd"
            }
        });
        
        if (response && response.error) {
            throw response.error;
        }
        
        return response.data || response;
    } catch (error) {
        console.error('Error audio transcript:', error);
        if (error && error.message != "Network Error") {
            logToSentry({
                message: 'Error audio transcript',
                error,
                module: 'SUBMISSIONS',
                operation: 'ADD_TIPS'
            });
        }
        return {};
    }
}

export const handleVideoTranscript = async (url) => {
    try {
        const response = await apis.post('assignmentsRDS', `/video_transcripting`, {
            body: {
                "audioUrl": url,
                "languageCode": "es-US",
                "bucket":config.isDev ?"aws-audio-transcript" : "aws-audio-transcript-prd"
            }
        });
        
        if (response && response.error) {
            throw response.error;
        }
        
        return response.data || response;
    } catch (error) {
        console.error('Error video transcript:', error);
        if (error && error.message != "Network Error") {
            logToSentry({
                message: 'Error video transcript',
                error,
                module: 'SUBMISSIONS',
                operation: 'VALIDATE_TEACHER'
            });
        }
        return {};
    }
}

export const handleGetTranscripts = async (urls) => {
    try {
        if (!urls || !Array.isArray(urls) || urls.length === 0) {
            return { transcripts: [] };
        }

        const response = await apis.post('assignmentsRDS', `/getTranscript`, {
            body: { urls: urls }
        });
        
        if (response && response.error) {
            throw response.error;
        }
        
        return response.data || response;
    } catch (error) {
        console.error('Error getting transcripts:', error);
        if (error && error.message != "Network Error") {
            logToSentry({
                message: 'Error getting transcripts',
                error,
                module: 'SUBMISSIONS',
                operation: 'GET_TRANSCRIPTS'
            });
        }
        return { transcripts: [] };
    }
}


export const handleGetSumissionById= (obj) => async (dispatch) => {
    const { userId, classId, submissionId } = obj || {};
    if (!userId || !classId || !submissionId) {
        return null;
    }
    try{
        const submission = await apis.post('assignmentsRDS', `/user_current_submission`,{
            body: { userId, classId, submissionId }
        });
        
        if (submission && submission.error) {
            throw submission.error;
        }
        
        if(submission?.success){
            return submission?.data;
        }
        throw new Error("Failed to get submission by id");
    }catch(error){
        console.error('Error getting submission by id:', error);
        if (error && error.message != "Network Error") {
            logToSentry({
                message: 'Error getting submission by id',
                error,
                module: 'SUBMISSIONS',
                operation: 'GET_SUBMISSION_BY_ID'
            });
        }
        return null;
    }
}

export const handleRefreshUserSubmissionInClass = ({ userId, classId, submissionId }) => async (dispatch) => {
    if (!userId || !classId || !submissionId) {
        return null;
    }
    try {
        const response = await apis.post('assignmentsRDS', `/user_current_submission`, {
            body: { userId, classId, submissionId },
        });

        if (response && response.error) {
            throw response.error;
        }

        if (response?.success && response?.data) {
            await dispatch(SubmissionsActions.updateSubmissionSuccess(response.data));
            return response.data;
        }
        return null;
    } catch (error) {
        console.error('Error refreshing user submission:', error);
        if (error && error.message != "Network Error") {
            logToSentry({
                message: 'Error refreshing user submission',
                error,
                module: 'SUBMISSIONS',
                operation: 'REFRESH_USER_SUBMISSION_IN_CLASS'
            });
        }
        return null;
    }
};


export const handleListSubmissionsByAssignmentAndClass = (assignmentId, classId) => async (dispatch) => {
    try {
        const response = await apis.get('assignmentsRDS', `/submissionByAssignment/${assignmentId}/${classId}`);
        if (response && response.success && response.data) {
            const data = response.data;
            if (Array.isArray(data)) {
                await dispatch(SubmissionsActions.updateSubmissionInBatchSuccess(data));
            }
            return data;
        }
        return response;
    } catch (error) {
        console.error('Error listing submissions by assignment and class:', error);
        if (error && error.message != "Network Error") {
            logToSentry({
                message: 'Error listing submissions by assignment and class',
                error,
                module: 'SUBMISSIONS',
                operation: 'LIST_SUBMISSIONS_BY_ASSIGNMENT_AND_CLASS'
            });
        }
    }
}

